import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import { ketNumberMap } from './ketNumbering';

const FormRow = ({ question, questionNumber, answer, handleInputChange, showHint, isReviewMode, isHydrated }) => {
  const isExample = question.points === 0;
  // Strip example markers and render {{gap}}/{{n}} tokens as a visible blank —
  // the actual typing happens in the input on the right.
  const cleanQuestionText = question.question_text
    .replace(/\(Example\)/gi, '')
    .replace(/\{\{[^}]*\}\}/g, '_____')
    .trim();

  // Split label by ':' to align the label and the value part (which may contain the gap)
  const labelParts = cleanQuestionText.split(':');
  const hasColon = labelParts.length > 1;
  const leftPart = labelParts[0];
  const rightPart = hasColon ? labelParts.slice(1).join(':') : null;

  const isAnswerCorrect = question.is_correct === true || question.is_correct === 'true';
  const showIncorrectAnswerInReview = isReviewMode && !isExample && !isAnswerCorrect;

  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 py-4 border-b border-slate-100 dark:border-slate-700 last:border-0 w-full overflow-hidden">
      <div className="flex items-baseline gap-3 flex-grow min-w-0">
        <span className="w-12 font-bold text-primary-blue text-sm flex-shrink-0 leading-tight">
          {isExample
            ? <span className="font-black text-white bg-amber-500 px-2 py-1 rounded-lg text-[10px] uppercase tracking-wider shadow-sm">Ví dụ</span>
            : `${questionNumber}.`}
        </span>
        <label className="flex-1 flex flex-col sm:flex-row sm:justify-between sm:items-baseline font-body font-bold text-slate-600 dark:text-slate-300 pr-2 min-w-0" htmlFor={question.id}>
          <span className="text-left break-words leading-tight py-1">{leftPart}{hasColon ? ':' : ''}</span>
          {rightPart && <span className="text-right opacity-80 sm:ml-2 italic text-sm leading-tight shrink-0 py-1">{rightPart}</span>}
        </label>
      </div>
      <div className="w-full md:w-auto md:min-w-[14rem] lg:min-w-[18rem] flex-shrink-0 flex items-center gap-2">
        {isExample ? (
          <div className="flex-grow px-3 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-xl border-2 border-transparent font-bold text-primary-blue/60 italic text-center">
            {question.accepted_answers && question.accepted_answers[0]}
          </div>
        ) : (
          <div className="flex-grow flex items-center gap-2 min-w-0">
            {showIncorrectAnswerInReview ? (
              <div className="exercise-input-text bg-red-50 px-3 py-1 rounded-lg flex-grow flex items-center min-w-0 h-11">
                {question.user_answer?.answer
                  ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold truncate max-w-[80px] sm:max-w-none">{question.user_answer.answer}</span>
                  : <span className="text-gray-400 mr-2 font-bold">__</span>
                }
                <span className="text-green-600 font-bold shrink-0">{question.accepted_answers[0]}</span>
              </div>
            ) : (
              <input
                type="text"
                id={question.id}
                autoComplete="off"
                value={showHint || (isReviewMode && isAnswerCorrect) ? question.accepted_answers[0] : (answer || '')}
                onChange={(e) => handleInputChange(question.id, e.target.value)}
                className={`flex-grow bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600 focus:ring-4 focus:ring-blue-100 focus:border-primary-blue rounded-xl px-4 py-2 font-bold text-primary-blue dark:text-blue-400 transition-all outline-none min-w-0 h-11 ${showHint ? 'hint-input' : ''}`}
                readOnly={showHint || !isHydrated}
                disabled={isReviewMode || !isHydrated}
              />
            )}
            {isReviewMode && (
              <div className="shrink-0 flex items-center gap-2">
                {isAnswerCorrect
                  ? <span className="correct-checkmark">✓</span>
                  : <span className="incorrect-checkmark">✗</span>
                }
                <QuestionLearnButton question={question} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Shared form card (title + prefilled rows + one input row per question) used by
// KET Listening Part 4/5 and Reading & Writing Part 8.
export const KetFormCard = ({ title, intro, prefilled, questions, numberMap, answers, onChange, showHint, isReviewMode, isHydrated }) => {
  const sortedQuestions = [...questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestion = sortedQuestions.find(q => q.points === 0);
  const mainQuestions = sortedQuestions.filter(q => q.points > 0);

  return (
    <div className="max-w-3xl mx-auto w-full bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-3 md:p-8 shadow-sm">
      {title && <h2 className="exercise-story-title text-center">{title}</h2>}
      {intro && <p className="text-center font-bold text-slate-500 dark:text-slate-400 mb-4">{intro}</p>}
      <div className="space-y-1 text-left">
        {/* Prefilled (non-editable) rows */}
        {(prefilled || []).map((row, index) => (
          <div key={index} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 py-3 border-b border-slate-100 dark:border-slate-700 pl-3 md:pl-[60px]">
            <span className="font-body font-bold text-slate-600 dark:text-slate-300">
              {row.label}{row.label && !String(row.label).trim().endsWith(':') ? ':' : ''}
            </span>
            <span className="font-black text-slate-800 dark:text-white">{row.value}</span>
          </div>
        ))}

        {exampleQuestion && (
          <FormRow
            question={exampleQuestion}
            answer={answers[exampleQuestion.id]}
            handleInputChange={onChange}
            isReviewMode={isReviewMode}
            isHydrated={isHydrated}
          />
        )}
        {mainQuestions.map((q, index) => (
          <FormRow
            key={q.id}
            question={q}
            questionNumber={(numberMap && numberMap[q.id]) || (q.order_index > 0 ? q.order_index : index + 1)}
            answer={answers[q.id]}
            handleInputChange={onChange}
            showHint={showHint}
            isReviewMode={isReviewMode}
            isHydrated={isHydrated}
          />
        ))}
      </div>
    </div>
  );
};

const KetListenPart4 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const { form_title, form_intro, prefilled } = exerciseData.content_data;

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10">
      <div className="relative flex flex-col h-full bg-transparent dark:from-slate-800/50 dark:to-card-dark rounded-2xl p-2 md:p-6 overflow-y-auto custom-scrollbar">
        <KetFormCard
          title={form_title}
          intro={form_intro}
          prefilled={prefilled}
          questions={exerciseData.questions}
          numberMap={ketNumberMap(exerciseData)}
          answers={answers}
          onChange={handleInputChange}
          showHint={showHint}
          isReviewMode={isReviewMode}
          isHydrated={isHydrated}
        />
      </div>
    </div>
  );
};

export default KetListenPart4;
